import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useResizeDetector } from 'react-resize-detector';
import styled from 'styled-components';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';

import { useSettlementRequestHook } from './hooks/useSettlementRequestHook';

import SettlementItem from './components/SettlementItem';
import SettlementFAQ from 'containers/Settlement/components/SettlementFAQ';
import SettlementRequestModal from './components/SettlementRequestModal';
import SettlementListModal from './components/SettlementListModal';

import {
  ICON_SETTLEMENT_ARROW_LEFT,
  ICON_SETTLEMENT_ARROW_RIGHT,
  ICON_SETTLEMENT_ARROW_LEFT_DISABLE,
  ICON_SETTLEMENT_ARROW_RIGHT_DISABLE,
  ICON_NAVIGATE_BACK,
  ICON_MENU_BUTTON,
  ICON_ARROW_BLACK,
} from 'assets/icons';
import SettlementValidationModal from 'containers/SettlementDetail/components/SettlementValidationModal';
import CommonImage from 'components/common/CommonImage';
import Text from 'components/common/Text';
import HackleManager from 'utils/hackle';
import Column from 'components/common/Column';
import Row from 'components/common/Row';
import BasicButton from 'components/Button/BasicButton';
import Loading from 'components/Loading/Loading';
import SettlementFailedContainer from './components/SettlementFailedContainer';
import {
  MENU_PATH,
  SETTLEMENT_DETAIL_PATH_DEFAULT,
  SETTLEMENT_LIST_PATH,
  SETTLEMENT_PATH,
  SETTLEMENT_REQUEST_LIST_PATH,
} from 'constants/path';
import { isEmpty } from 'lodash';
import WrapperContainer from 'components/common/Wrapper';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 auto;
  width: 100%;
  max-width: 768px;
`;

const Container = styled.div<{ $isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  padding: ${props => (props.$isMobile ? '0 20px 20px' : '0 40px')};
  margin: 0 auto;
  width: 100%;

  min-height: 100dvh;

  background-color: #fff;
`;

const NavigateContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;
  gap: 8px;
  padding: 12px 16px;
  width: 100%;
  border-radius: 12px;
  background: #f7f7f8;
`;

const SettlementListContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 32px;

  /* padding: 40px 20px; */
  width: 100%;
`;

const ArrowContainer = styled.div<{ $isActiveButton: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4px;

  border-radius: 50%;
  border: ${props =>
    props.$isActiveButton ? '1px solid #c2c4c8' : '1px solid #EAEBEC'};
  background: #fff;
`;

const ProvisionButton = styled.div<{ color: string; background: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 24px;

  border-radius: 8px;
  background: ${props => props.background};
  color: ${props => props.color};
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%; /* 24px */
  letter-spacing: -0.6px;
  cursor: pointer;
`;

const SettlementRequestPage = () => {
  const {
    isLoading,
    pageIndex,
    settlementList,
    settlementItem,
    handleNextSettlementItem,
    handlePrevSettlementItem,
    handleSelectSettlementItem,
    handleRequestInvoiceSettlement,
  } = useSettlementRequestHook();
  dayjs.locale('ko');

  const navigate = useNavigate();

  const { width, ref } = useResizeDetector();

  const isMobile = width ? width <= 768 : window.innerWidth <= 768;

  const [isOpenAllSettlementItemModal, setIsOpenAllSettlementItemModal] =
    useState<boolean>(false);
  const [isOpenSettlementRequestModal, setIsOpenSettlementRequestModal] =
    useState<boolean>(false);
  const [
    isOpenSettlementInformationModal,
    setIsOpenSettlementInformationModal,
  ] = useState<boolean>(false);
  // const [isOpenCommonDialog, setIsOpenCommonDialog] = useState<boolean>(false);

  const isHaveMinusSettlement = settlementList.some(
    item =>
      Number(item.node.totalPayment.value) < 0 && item.node.invoice === null,
  );

  const handlePageBackwardNavigate = () => {
    if (isOpenSettlementInformationModal) {
      setIsOpenSettlementInformationModal(false);

      return;
    }

    if (isOpenSettlementRequestModal) {
      setIsOpenSettlementRequestModal(false);

      return;
    }

    navigate(MENU_PATH);
  };

  useEffect(() => {
    window.handlePageBackwardNavigate = handlePageBackwardNavigate;
  }, [isOpenSettlementRequestModal, isOpenSettlementInformationModal]);

  useEffect(() => {
    HackleManager.logSettlementListView();
  }, []);

  const renderHeader = (): JSX.Element => {
    return (
      <Column align="flex-start" style={{ marginBottom: '8px' }}>
        <Row
          gap="16px"
          justifyContent="flex-start"
          style={{
            padding: isMobile ? '32px 0 24px' : '40px 0',
            width: '100%',
            height: isMobile ? '84px' : '100px',
          }}
        >
          <CommonImage
            src={ICON_NAVIGATE_BACK}
            onClick={handlePageBackwardNavigate}
          />
          <Text color="#1b1c1e" size="24px" style={{ lineHeight: '32px' }}>
            정산 신청
          </Text>
        </Row>
        <NavigateContainer>
          <Row
            style={{ width: '32%', cursor: 'pointer' }}
            onClick={() => {
              navigate(SETTLEMENT_PATH);
            }}
          >
            <Text color="#1b1c1e" style={{ cursor: 'pointer' }}>
              정산 정보 관리
            </Text>
          </Row>
          <Text>|</Text>
          <Row
            style={{ width: '32%' }}
            onClick={() => {
              navigate(SETTLEMENT_REQUEST_LIST_PATH);
            }}
          >
            <Text color="#1b1c1e" style={{ cursor: 'pointer' }}>
              지급 요청 내역
            </Text>
          </Row>
          <Text>|</Text>
          <Row
            gap="4px"
            style={{ width: '32%' }}
            onClick={() => {
              navigate(SETTLEMENT_LIST_PATH);
            }}
          >
            <CommonImage
              src={ICON_MENU_BUTTON}
              onClick={() => {
                navigate(SETTLEMENT_LIST_PATH);
              }}
              style={{ width: '16px' }}
            />
            <Text color="#1b1c1e" style={{ cursor: 'pointer' }}>
              전체보기
            </Text>
          </Row>
        </NavigateContainer>
      </Column>
    );
  };

  if (isLoading) {
    return <Loading />;
  }

  if (!isLoading && isEmpty(settlementList)) {
    return <SettlementValidationModal settlementItem={settlementItem} />;
  }

  return (
    <WrapperContainer isMobile={isMobile}>
      <Wrapper ref={ref}>
        <Container $isMobile={isMobile}>
          {renderHeader()}
          {isHaveMinusSettlement && (
            <SettlementFailedContainer isMobile={isMobile} />
          )}
          <SettlementListContainer>
            <Row
              justifyContent="space-between"
              align="flex-start"
              gap="8px"
              style={{ width: '100%' }}
            >
              <SettlementItem settlementItem={settlementItem!.node} />
              <Row gap="8px" style={{ paddingTop: '15px' }}>
                <ArrowContainer
                  $isActiveButton={
                    settlementList.length !== 1 &&
                    pageIndex < settlementList.length - 1
                  }
                  onClick={handleNextSettlementItem}
                >
                  <img
                    src={
                      settlementList.length !== 1 &&
                      pageIndex < settlementList.length - 1
                        ? ICON_SETTLEMENT_ARROW_LEFT
                        : ICON_SETTLEMENT_ARROW_LEFT_DISABLE
                    }
                    alt="icon settlement arrow left"
                  />
                </ArrowContainer>
                <ArrowContainer
                  $isActiveButton={pageIndex !== 0}
                  onClick={handlePrevSettlementItem}
                >
                  <img
                    src={
                      pageIndex !== 0
                        ? ICON_SETTLEMENT_ARROW_RIGHT
                        : ICON_SETTLEMENT_ARROW_RIGHT_DISABLE
                    }
                    alt="icon settlement arrow right"
                  />
                </ArrowContainer>
              </Row>
            </Row>
            <Row
              justifyContent="space-between"
              gap="24px"
              style={{ width: '100%' }}
            >
              <Row gap="8px">
                <BasicButton
                  content="정산 미리보기"
                  onClick={() => {
                    setIsOpenSettlementInformationModal(true);
                  }}
                />
                <BasicButton
                  content={
                    <Row>
                      <span>정산 내역</span>
                      <CommonImage src={ICON_ARROW_BLACK} onClick={() => {}} />
                    </Row>
                  }
                  onClick={() => {
                    navigate(
                      `${SETTLEMENT_DETAIL_PATH_DEFAULT}/${settlementItem.node.id}`,
                    );
                  }}
                  style={{ paddingRight: '8px' }}
                />
              </Row>
              {settlementItem.node.invoice === null &&
                Number(settlementItem.node.totalPayment.value) +
                  Number(settlementItem.node.totalFee.value) >=
                  0 && (
                  <ProvisionButton
                    color={isHaveMinusSettlement ? '#DBDCDF' : '#FFF'}
                    background={isHaveMinusSettlement ? '#F4F4F5' : '#1B1C1E'}
                    onClick={() => {
                      if (!isHaveMinusSettlement) {
                        setIsOpenSettlementRequestModal(true);
                      }

                      // if (isHaveMinusSettlement) {
                      //   setIsOpenCommonDialog(true);
                      // } else {
                      //   setIsOpenSettlementRequestModal(true);
                      // }
                    }}
                  >
                    지급 신청
                  </ProvisionButton>
                )}
            </Row>
            <SettlementFAQ />
          </SettlementListContainer>
        </Container>
        {isOpenSettlementInformationModal && (
          <SettlementRequestModal
            isMobile={isMobile}
            type="information"
            settlementItem={settlementItem!.node}
            onClose={() => setIsOpenSettlementInformationModal(false)}
          />
        )}
        {isOpenSettlementRequestModal && (
          <SettlementRequestModal
            isMobile={isMobile}
            type="request"
            settlementItem={settlementItem!.node}
            handleRequestInvoice={handleRequestInvoiceSettlement}
            onClose={() => setIsOpenSettlementRequestModal(false)}
          />
        )}
        {isOpenAllSettlementItemModal && (
          <SettlementListModal
            settlementList={settlementList}
            handleSelectSettlementItem={handleSelectSettlementItem}
            onClose={() => {
              setIsOpenAllSettlementItemModal(false);
            }}
          />
        )}
      </Wrapper>
    </WrapperContainer>
  );
};

export default SettlementRequestPage;
